import React, { useState, useEffect } from 'react';
import styles from './MultiTab.module.css';

const MultiTab = ({ tabs = [], onChange, selectedTab, onTabChange, onAdd, onDelete }) => {
  const safeTabs = Array.isArray(tabs) && tabs.length > 0 ? tabs : [];
  const [activeTab, setActiveTab] = useState(() => (selectedTab ?? safeTabs[0] ?? null));

  // Sync with parent when controlled; otherwise ensure a valid default
  useEffect(() => {
    const isControlled = selectedTab !== undefined && selectedTab !== null;
    if (isControlled) {
      if (selectedTab !== activeTab) {
        setActiveTab(selectedTab);
      }
      return;
    }

    if (!safeTabs.length) {
      if (activeTab !== null) setActiveTab(null);
      return;
    }
    if (!activeTab || !safeTabs.includes(activeTab)) {
      setActiveTab(safeTabs[0]);
    }
  }, [selectedTab, safeTabs, activeTab]);

  useEffect(() => {
    const isControlled = selectedTab !== undefined && selectedTab !== null;
    if (!isControlled && onChange && activeTab) {
      onChange(activeTab);
    }
  }, [activeTab, onChange, selectedTab]);

  const handleDelete = (tabToDelete, event) => {
    event.stopPropagation(); // Prevent tab selection when clicking delete
    
    if (window.confirm(`Are you sure you want to delete "${tabToDelete}"?`)) {
      if (onDelete) {
        onDelete(tabToDelete);
      }
      
      // If the deleted tab was active, switch to another tab
      if (activeTab === tabToDelete) {
        const remainingTabs = safeTabs.filter(tab => tab !== tabToDelete);
        if (remainingTabs.length > 0) {
          setActiveTab(remainingTabs[0]);
          onTabChange && onTabChange(remainingTabs[0]);
        } else {
          setActiveTab(null);
        }
      }
    }
  };

  return (
    <div className={styles.tabContainer}>
      <div className={styles.tabList} role="tablist" aria-label="Dashboard sections">
        {safeTabs.map((tab) => (
          <div key={tab} className={styles.tabWrapper}>
            <button
              role="tab"
              aria-selected={activeTab === tab}
              className={`${styles.tabButton} ${activeTab === tab ? styles.active : ''}`}
              onClick={() => {
                setActiveTab(tab);
                onTabChange && onTabChange(tab);
              }}
              type="button"
            >
              {tab}
            </button>
            {onDelete && (
              <button
                type="button"
                className={styles.deleteButton}
                aria-label={`Delete ${tab}`}
                onClick={(event) => handleDelete(tab, event)}
                title={`Delete ${tab}`}
              >
                ×
              </button>
            )}
          </div>
        ))}
        {onAdd && (
          <button
            type="button"
            className={styles.addButton}
            aria-label="Add report"
            onClick={() => onAdd()}
          >
            ＋ Add
          </button>
        )}
      </div>
    </div>
  );
};

export default MultiTab;


